/**
 * Diario.tsx — los asientos registrados, y el mayor de una cuenta.
 *
 * Es la otra mitad del hito de E1: un asiento cuadra y **aparece en diario y
 * mayor**. Lo que se ve acá viene de la base, no de un fixture.
 *
 * ── Por qué las líneas se ven siempre ─────────────────────────────────────
 *
 * Cada asiento muestra su detalle sin tener que abrirlo. Un diario que enseña
 * solo cabeceras y totales deja pasar el asiento que cuadra sumando dos veces
 * la misma cuenta — cuadra, y está mal. El detalle es el diario; el resumen es
 * una comodidad.
 *
 * ── Y por qué no hay botón de editar ──────────────────────────────────────
 *
 * Porque no existe la operación. `app_api` tiene `select` e `insert` sobre el
 * diario y nada más: un asiento se corrige con otro asiento. Poner el botón y
 * que fallara sería peor que no tenerlo.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EnlaceAlAsiento } from '../componentes/EnlaceAlAsiento.tsx';
import { Texto, useIdioma } from '../nucleo/idioma.tsx';
import { puedeRegistrar, useSesion } from '../nucleo/sesion.tsx';
import { useCatalogo, nombreDe } from '../nucleo/catalogo.tsx';
import { mostrar, type Importe } from '../nucleo/dinero.ts';
import { anioYMesDeHoy } from '../nucleo/hoy.ts';
import {
  corregirFechaAsiento, traerDiario, traerMayor, FalloDeApi,
  type AsientoReal, type MovimientoMayor,
} from '../datos/libros.ts';

/** Los doce meses, y el 0 que significa «el año entero». */
const MESES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;


export function Diario() {
  const { t, idioma } = useIdioma();
  const { pedir, sesion } = useSesion();
  const { cuentas } = useCatalogo();
  const escribe = puedeRegistrar(sesion);

  /* ▶▶ EDICIÓN DE FECHA (`0124`). Solo asientos de origen `manual` admiten
     corrección de fecha —la casa adoptó el principio de inmutabilidad para
     cuentas, importes y clasificación— y hace falta un motivo que queda en la
     auditoría. El modal pide los dos campos antes de disparar; sin motivo el
     botón queda apagado. */
  const [corrigiendo, setCorrigiendo] = useState<string | null>(null);
  const [nuevaFecha, setNuevaFecha] = useState('');
  const [motivoCorreccion, setMotivoCorreccion] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [avisoCorreccion, setAvisoCorreccion] = useState<string | null>(null);

  /**
   * ▶ LA MONEDA SALE DE LA EMPRESA, y acá había un `const MONEDA_LOCAL = 'CRC'`.
   *
   * Es el último sitio con la moneda escrita a mano. El resto del producto lo
   * arregló al borrar `datos/muestra.ts` —de donde salía ese `'CRC'` de
   * ejemplo—, y esta pantalla se quedó con su propia copia.
   *
   * Para una empresa que lleva sus libros en dólares, el diario rotulaba cada
   * asiento en colones. La cifra correcta con la moneda equivocada es peor que
   * ninguna cifra: se puede leer, apuntar y sumar.
   */
  const moneda = sesion?.empresa.monedaLocal ?? '';

  /** Un importe de la API listo para `mostrar`. Nunca pasa por `Number`. */
  const imp = (valor: string): Importe => ({ valor, moneda });



  const [asientos, setAsientos] = useState<readonly AsientoReal[] | null>(null);
  const [recorte, setRecorte] = useState<{ hayMas: boolean; limite: number } | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [cuenta, setCuenta] = useState('');
  const [mayor, setMayor] = useState<readonly MovimientoMayor[] | null>(null);
  const [recorteMayor, setRecorteMayor] = useState<{ hayMas: boolean; limite: number } | null>(null);

  /**
   * ▶ EL PERIODO, QUE FALTABA — Y NO PORQUE NO SE PUDIERA.
   *
   * `control.diario(anio, mes)` acepta periodo desde la 0012 y `/api/asientos`
   * lo lee. Esta pantalla llamaba `traerDiario(pedir)` **sin pasarlo**, o sea
   * que pedía todos los asientos de la historia de la empresa con sus líneas
   * dentro. Medido con la demo: 1,2 kB por asiento, sin techo. Un cliente que
   * registre trescientos al mes se lleva cuatro megas en la primera pantalla.
   *
   * El filtro estaba en la base y en el endpoint, y se moría acá. Es el
   * guardián 8 un piso más arriba: no una función concedida sin llamador, sino
   * un PARÁMETRO aceptado que nadie mandaba nunca.
   *
   * Arranca en el mes en curso porque es lo que alguien viene a mirar cuando
   * abre el diario. El «año entero» sigue estando, a un clic, con su techo
   * detrás.
   */
  /* ══════════════════════════════════════════════════════════════════════
     ▶▶ EL DIARIO YA SABE RECIBIR UN ASIENTO — y el enlace lo esperaba.
     ══════════════════════════════════════════════════════════════════════

     `/por-pagar` y `/por-cobrar` enseñaban el número de asiento **sin enlace**,
     con su motivo escrito al lado: *el diario no lee `?asiento=`, filtra por
     año y mes, así que un enlace abriría la pantalla y no llevaría a ninguna
     parte*. El motivo era cierto. Ahora deja de serlo.

     ▶ Y lo que llega NO es sólo el número: llegan **año, mes y número**. Mandar
     el número solo habría sido el mismo defecto con otra cara —el diario abre
     en el mes en curso, y un asiento de marzo no está ahí—. Quien enlaza sabe
     de qué fecha es el asiento, así que lo dice.

     ▶ Y es el estado INICIAL, no un `useEffect` que vigile la URL: si la URL
     mandara siempre, cambiar de periodo con el desplegable volvería solo al
     periodo del enlace y la pantalla se sentiría trabada. Se entra donde el
     enlace dice y después manda la persona. */
  const hoyEs = anioYMesDeHoy();
  const [busca] = useSearchParams();

  /** Un entero de la URL, o `null`. Nunca `NaN`, que dibujaría «NaN». */
  const dePregunta = (clave: string): number | null => {
    const crudo = busca.get(clave);
    if (crudo === null || crudo.trim() === '') return null;
    const n = Number(crudo);
    return Number.isInteger(n) ? n : null;
  };

  const pedido = dePregunta('asiento');
  const [anio, setAnio] = useState(dePregunta('anio') ?? hoyEs.anio);
  const [mes, setMes] = useState<number>(dePregunta('mes') ?? hoyEs.mes);

  /**
   * El asiento señalado, que deja de estarlo en cuanto la persona se mueve.
   *
   * ▶ Dejarlo pegado sería peor que no señalar nada: al cambiar de mes el
   * resaltado apuntaría a una fila que ya no es la que se buscó, o a ninguna,
   * y seguiría diciendo «es éste».
   */
  const [senalado, setSenalado] = useState<number | null>(pedido);
  const yaFue = useRef(false);

  const empresa = sesion?.empresa.id ?? null;

  // Cambiar de periodo apaga el señalado: fuera de su mes, ese número ya no
  // está en la lista y la marca señalaría a otro.
  const verAnio = (n: number) => { setAnio(n); setSenalado(null); };
  const verMes = (n: number) => { setMes(n); setSenalado(null); };

  const cargar = useCallback(async () => {
    setFallo(null);
    try {
      // `mes === 0` es «el año entero»: se manda `undefined` y la base lo
      // entiende como «sin filtrar por mes», que es lo que ya sabía hacer.
      const r = await traerDiario(pedir, anio, mes === 0 ? undefined : mes);
      setAsientos(r.filas);
      setRecorte({ hayMas: r.hayMas, limite: r.limite });
    } catch (e) {
      setAsientos([]);
      setRecorte(null);
      setFallo(e instanceof FalloDeApi && e.clase === 'conexion'
        ? 'sb.conexion' : 'sb.unknown');
    }
  }, [pedir, anio, mes]);

  useEffect(() => { if (empresa) void cargar(); }, [empresa, cargar]);

  useEffect(() => {
    if (!cuenta) { setMayor(null); return; }
    let vivo = true;
    (async () => {
      try {
        // ▶ El mayor va por el MISMO periodo que el diario, y no por su cuenta.
        //
        // Dos filtros para una pantalla —uno arriba y otro escondido en la
        // caja del mayor— son dos hechos que se ven iguales: quien mire el
        // diario de setiembre y el mayor de la cuenta creería que está
        // mirando el mismo periodo, y no habría nada que le dijera que no.
        const m = await traerMayor(pedir, cuenta, anio, mes === 0 ? undefined : mes);
        if (vivo) {
          setMayor(m.filas);
          setRecorteMayor({ hayMas: m.hayMas, limite: m.limite });
        }
      } catch {
        if (vivo) { setMayor([]); setRecorteMayor(null); }
      }
    })();
    return () => { vivo = false; };
  }, [cuenta, pedir, anio, mes]);

  const hojas = useMemo(
    () => cuentas.filter((c) => c.permiteMovimiento),
    [cuentas],
  );

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>{t('jr.title')}</h1>
          <p className="page-head__sub">{t('jr.sub')}</p>
        </div>
        <div className="page-actions">
          {/* El mismo par año+mes que Reportes, y a propósito: dos selectores
              de periodo distintos en el mismo producto se aprenden dos veces. */}
          <label className="field field--inline">
            <span className="field__label">{t('jr.period')}</span>
            <select
              className="select" value={`${anio}-${mes}`}
              onChange={(e) => {
                const [a, m] = e.target.value.split('-');
                verAnio(Number(a));
                verMes(Number(m));
              }}
            >
              {MESES.map((m) => (
                <option key={m} value={`${anio}-${m}`}>
                  {m === 0
                    ? t('jr.wholeYear', { anio: String(anio) })
                    : `${anio}-${String(m).padStart(2, '0')}`}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {fallo && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t(fallo as never)}</span>
        </div>
      )}

      <div className="stack">
        {/* ── El diario ── */}
        <section className="card card--pad0">
          <div className="card__head">
            <h3>{t('jr.title')}</h3>
            <span className="spacer" />
            {/* ▶ El conteo dice DE QUÉ PERIODO, porque ya no son todos.
                Un «70 asientos» a secas era cierto cuando la pantalla traía la
                historia entera; con un filtro puesto pasa a ser la mitad de un
                hecho, y la mitad que falta es justo la que importó poner. */}
            <span className="small muted">
              {asientos === null
                ? t('common.loading')
                : asientos.length === 1
                  ? t('jr.countOne')
                  : t('jr.count', { asientos: String(asientos.length) })}
              {recorte?.hayMas === true && (
                <span className="chip chip--warn" style={{ marginLeft: 'var(--s4)' }}>
                  {t('jr.trimmed', { limite: String(recorte.limite) })}
                </span>
              )}
            </span>
          </div>

          {asientos !== null && asientos.length === 0 && (
            <p className="muted" style={{ padding: 'var(--s7)' }}>{t('jr.empty')}</p>
          )}

          {/* ▶ SE PIDIÓ UN ASIENTO Y NO ESTÁ: se dice, no se calla.
              Un enlace que abre el diario en un periodo donde ese número no
              aparece deja a la persona buscando una fila que no existe, con la
              pantalla en verde. Pasa si el asiento se anuló, o si alguien
              teclea el número a mano en la barra de direcciones. */}
          {senalado !== null && asientos !== null
            && !asientos.some((a) => Number(a.numero) === senalado) && (
            <p className="alert alert--warn" style={{ margin: 'var(--s6) var(--s7)' }}>
              <span>{t('jr.notHere', { asiento: String(senalado) })}</span>
            </p>
          )}

          {avisoCorreccion && (
            <div className="alert alert--ok" style={{ marginBottom: 'var(--s5)' }}>
              <span>{avisoCorreccion}</span>
              <button
                type="button" className="btn btn--ghost btn--sm"
                onClick={() => setAvisoCorreccion(null)}
                style={{ marginLeft: 'var(--s4)' }}
              >
                {t('common.dismiss')}
              </button>
            </div>
          )}

          {asientos?.map((a) => (
            <div
              key={a.numero}
              className={`asiento${Number(a.numero) === senalado ? ' asiento--senalado' : ''}`}
              ref={(el) => {
                // Se trae a la vista UNA sola vez. Sin el `yaFue`, cada
                // repintado volvería a empujar la página hacia esta fila y no
                // se podría leer lo de abajo.
                if (el === null || Number(a.numero) !== senalado || yaFue.current) return;
                yaFue.current = true;
                el.scrollIntoView({ block: 'center', behavior: 'smooth' });
              }}
            >
              <div className="asiento__head">
                <span className="mono small">#{a.numero}</span>
                {/* La fecha se muestra tal cual viene: es texto `YYYY-MM-DD`.
                    Pasarla por `new Date` para «darle formato» la mueve de día
                    en cualquier huso al oeste de Greenwich. */}
                <span className="mono small muted-2">{a.fecha}</span>
                <strong>{a.glosa}</strong>
                <span className="spacer" />
                {/* ▶ CUÁNDO SE TECLEÓ, que no es la fecha contable.
                    Un asiento de marzo escrito en junio es legítimo y es
                    información: en una revisión, el segundo es el dato que se
                    pregunta. La columna existía desde E1 y no la leía nadie —
                    lo encontró el guardián 9. */}
                <span className="xs muted" title={t('jr.recordedAt')}>
                  {a.registrado_en.slice(0, 10)}
                </span>
                {/* ▶ Y QUIÉN LO ESCRIBIÓ, que va pegado al cuándo porque es el
                    otro medio del mismo par. `creado_por` estaba guardado desde
                    E1 y no salía por ningún lado — y el pie de «Usuarios de la
                    empresa» lo prometía en voz alta: «su nombre sigue en la
                    bitácora y en cada asiento suyo». Era la mitad falsa.

                    Los dos «sin nombre» se dicen distinto, como en la bitácora:
                    sin uuid lo escribió una herramienta; con uuid y sin fila,
                    la persona se borró. El correo va en el título porque dos
                    personas se pueden llamar igual, y no en la línea porque
                    aquí lo que se busca es recorrer, no identificar. */}
                <span
                  className="xs muted" title={a.correo ?? t('jr.who')}
                >
                  {a.quien
                    // ▶ `== null` y no `=== null`, a propósito: si algún día el
                    // campo deja de llegar, que caiga del lado que no acusa a
                    // nadie. Decir «el sistema» de más es vago; decir «usuario
                    // retirado» de más señala a una persona que no se fue.
                    ?? t(a.creado_por == null ? 'common.system' : 'common.removedUser')}
                </span>
                <span className="amount">{mostrar(imp(a.total), idioma)}</span>
                {/* ▶▶ BOTÓN DE CORRECCIÓN DE FECHA (`0124`). Solo asientos
                    MANUALES: lo que vino de otro módulo se corrige desde allí
                    (SB065) — un pago desde la antigüedad del documento, un
                    devengo volviendo a correr `devengar_periodo`, etc. */}
                {escribe && a.origen === 'manual' && (
                  <button
                    className="btn btn--ghost btn--sm"
                    type="button"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      setCorrigiendo(a.numero);
                      setNuevaFecha(a.fecha);
                      setMotivoCorreccion('');
                    }}
                    title={t('jr.editDateHint')}
                  >
                    {t('jr.editDate')}
                  </button>
                )}
              </div>

              {/* ▶ El modal pide los dos campos —fecha y motivo— pegado al
                  asiento, para que el contexto no se pierda. Sin motivo el botón
                  queda apagado: una corrección sin motivo no se guarda. */}
              {corrigiendo === a.numero && (
                <div className="alert alert--info" style={{ margin: 'var(--s4)' }}>
                  <div className="grid grid--form" style={{ marginBottom: 'var(--s4)' }}>
                    <label className="field">
                      <span className="field__label">{t('jr.newDate')}</span>
                      <input
                        className="input" type="date"
                        value={nuevaFecha}
                        onChange={(ev) => setNuevaFecha(ev.target.value)}
                      />
                    </label>
                    <label className="field">
                      <span className="field__label">{t('jr.editReason')}</span>
                      <input
                        className="input"
                        value={motivoCorreccion}
                        placeholder={t('jr.editReasonHint')}
                        onChange={(ev) => setMotivoCorreccion(ev.target.value)}
                      />
                    </label>
                  </div>
                  <div className="row" style={{ gap: 'var(--s3)' }}>
                    <button
                      className="btn btn--primary btn--sm"
                      type="button"
                      disabled={ocupado
                        || nuevaFecha === '' || nuevaFecha === a.fecha
                        || motivoCorreccion.trim() === ''}
                      onClick={async () => {
                        setOcupado(true);
                        try {
                          await corregirFechaAsiento(
                            pedir, a.numero, nuevaFecha, motivoCorreccion.trim());
                          setAvisoCorreccion(t('jr.editDone', { numero: a.numero }));
                          setCorrigiendo(null);
                          await cargar();
                        } catch (err) {
                          const codigo = err instanceof FalloDeApi
                            ? err.codigo : 'rechazado';
                          setAvisoCorreccion(
                            t(`sb.${codigo}` as never, { fallback: String(err) }));
                        } finally {
                          setOcupado(false);
                        }
                      }}
                    >
                      {t('jr.editSave')}
                    </button>
                    <button
                      className="btn btn--ghost btn--sm"
                      type="button"
                      onClick={() => { setCorrigiendo(null); setMotivoCorreccion(''); }}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              )}
              <div className="table-wrap">
                <table className="lines">
                  <thead>
                    <tr>
                      <th className="col-acc">{t('jr.account')}</th>
                      <th>{t('form.lineDetail')}</th>
                      <th className="col-amt" style={{ textAlign: 'right' }}>{t('common.debit')}</th>
                      <th className="col-amt" style={{ textAlign: 'right' }}>{t('common.credit')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.lineas.map((l) => (
                      <tr key={l.linea}>
                        <td className="mono small">
                          {l.cuenta} · {idioma === 'en' ? l.nombreEn : l.nombre}
                        </td>
                        <td className="small muted">
                          {l.detalle}
                          {Object.entries(l.clasificacion).map(([k, v]) => (
                            <span key={k} className="chip chip--soft" style={{ marginLeft: 6 }}>
                              {t(`cls.${k}` as never)}: {v}
                            </span>
                          ))}
                        </td>
                        <td className="amount" style={{ textAlign: 'right' }}>
                          {l.debe === '0.00' ? '' : (
                            <>
                              {mostrar(imp(l.debe), idioma)}
                              {l.moneda !== moneda && (
                                <div className="small muted">
                                  {mostrar({ valor: l.debe_original,
                                             moneda: l.moneda }, idioma)}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                        <td className="amount" style={{ textAlign: 'right' }}>
                          {l.haber === '0.00' ? '' : (
                            <>
                              {mostrar(imp(l.haber), idioma)}
                              {l.moneda !== moneda && (
                                <div className="small muted">
                                  {mostrar({ valor: l.haber_original,
                                             moneda: l.moneda }, idioma)}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </section>

        {/* ── El mayor ── */}
        <section className="card card--pad0">
          <div className="card__head">
            <h3>{t('jr.ledger')}</h3>
            <span className="spacer" />
            <select
              className="select" value={cuenta}
              onChange={(e) => setCuenta(e.target.value)}
              style={{ maxWidth: 340 }}
            >
              <option value="">{t('jr.pickAccount')}</option>
              {hojas.map((c) => (
                <option key={c.codigo} value={c.codigo}>
                  {c.codigo} · {nombreDe(c, idioma)}
                </option>
              ))}
            </select>
          </div>

          {/* ▶ ACÁ EL AVISO ES UN CARTEL Y NO UN CHIP, y la diferencia no es
              de estilo. El mayor lleva SALDO CORRIDO: una lista cortada con
              saldo corrido parece completa, porque la última fila enseña un
              saldo perfectamente creíble que no es el saldo de la cuenta.
              La base corta por el final para que las filas visibles lleven su
              saldo verdadero — pero eso solo sirve si quien la lee sabe que
              faltan las de después. */}
          {cuenta && recorteMayor?.hayMas === true && (
            <div className="alert alert--warn" style={{ margin: 'var(--s5)' }}>
              {/* `Texto` y no `t()`: esta frase lleva énfasis, y `t()` pintaría
                  los asteriscos tal cual. Lo cazó la prueba `sinEnfasis`. */}
              <span><Texto k="jr.ledgerTrimmed" con={{ limite: String(recorteMayor.limite) }} /></span>
            </div>
          )}

          {cuenta && mayor !== null && mayor.length === 0 && (
            <p className="muted" style={{ padding: 'var(--s7)' }}>{t('jr.empty')}</p>
          )}

          {mayor !== null && mayor.length > 0 && (
            <div className="table-wrap table-wrap--alto">
              <table className="lines">
                <thead>
                  <tr>
                    <th>{t('jr.date')}</th>
                    <th>{t('jr.memo')}</th>
                    <th className="col-amt" style={{ textAlign: 'right' }}>{t('common.debit')}</th>
                    <th className="col-amt" style={{ textAlign: 'right' }}>{t('common.credit')}</th>
                    <th className="col-amt saldo" style={{ textAlign: 'right' }}>{t('jr.balance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {mayor.map((m) => (
                    <tr key={`${m.asiento}-${m.linea}`}>
                      <td className="mono small">{m.fecha}</td>
                      <td className="small">
                        {/* ▶ Del mayor al asiento entero. Cae en esta misma
                            pantalla y por eso parece de menos valor — no lo es:
                            el mayor enseña UNA línea de un asiento, y lo que se
                            quiere ver es con qué cuadra. */}
                        <EnlaceAlAsiento asiento={m.asiento} fecha={m.fecha} /> {m.glosa}
                      </td>
                      <td className="amount" style={{ textAlign: 'right' }}>
                        {m.debe === '0.00' ? '' : mostrar(imp(m.debe), idioma)}
                      </td>
                      <td className="amount" style={{ textAlign: 'right' }}>
                        {m.haber === '0.00' ? '' : mostrar(imp(m.haber), idioma)}
                      </td>
                      <td className="amount saldo" style={{ textAlign: 'right' }}>
                        {mostrar(imp(m.saldo), idioma)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}
